import { useEffect, useState } from "react";
import { api } from "./client";

/**
 * Beveiligde bestanden ophalen.
 *
 * Een `<iframe src>` of `<a href>` wordt door de browser zélf opgehaald, buiten
 * onze axios-client om. Daardoor gaat er geen `Authorization`-header mee en
 * levert dat met echte auth (SKIP_AUTH=false) een 401 op — in de iframe ziet de
 * gebruiker dan alleen "refused to connect". Daarom halen we de PDF hier op via
 * de API-client (die het bearer-token zet) en maken we er een object-URL van.
 *
 * Regressie 2026-09-18: de PDF-preview en de downloadknop braken zodra de
 * B2C-login op preview aanging.
 */

/** Haalt `path` op als blob en geeft er een object-URL voor terug. */
export function useAuthedBlobUrl(path: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!path) {
      setUrl(null);
      setError(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);

    api
      .get(path, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(resp.data as Blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 403
            ? "Je hebt geen toegang tot dit rapport."
            : "Het rapport kon niet geladen worden."
        );
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path]);

  return { url, error };
}

/** Haalt `path` op en laat de browser het opslaan onder `filename`. */
export async function downloadAuthedFile(path: string, filename: string): Promise<void> {
  const resp = await api.get(path, { responseType: "blob" });
  const objectUrl = URL.createObjectURL(resp.data as Blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Niet meteen intrekken: Chrome/Safari hebben de URL nog even nodig.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
}
